import type { Room } from '@studio/models/room';
import type { AgentRegistryDesktopBridgeCapacity } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import { AgentRegistryDesktopBridgeCapacityStatus } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type {
  BridgeSetupCapabilities,
  EnsureInitialRoomInventoryRoom
} from '#~/api/agent/registry.js';
import { toHexFromId } from '#~/api/index.js';
import type { AccountBootstrapPickerMode } from '#~/routes/root/account-bootstrap-decision.js';

export interface AccountStudioPickerBridge {
  readonly id: string;
  readonly name: string;
}

export type AccountStudioPickerCapacity =
  | { readonly kind: 'available'; readonly maximum: number }
  | { readonly kind: 'at_capacity'; readonly maximum: number }
  | { readonly kind: 'unknown' };

export interface AccountStudioPickerRoom {
  readonly roomId: Room['id'];
  /** Retained for the future organization-aware presentation; intentionally hidden today. */
  readonly organizationId: string;
  readonly activeBridges: readonly AccountStudioPickerBridge[];
  readonly canLinkBridge: boolean;
  /**
   * MultiBridge capacity is service-owned (ENG-4483 W2). Absent when the
   * server predates the field; the selection decision treats absence and
   * `unknown` alike: no admission, retry refreshes.
   */
  readonly desktopBridgeCapacity?: AccountStudioPickerCapacity | undefined;
}

/**
 * Wire capacity to picker capacity. Committed plus reserved occupancy decides
 * At capacity — a reservation another computer holds mid-handshake is a taken
 * slot even though no card names it. Only a RESOLVED status with a positive
 * integer maximum is a real limit: proto3 zeros are indistinguishable from an
 * omitted field, and a zero-slot Studio is contradictory for Iris, so both
 * fail closed to `unknown`. Per rule: reference-rules-in-comments.
 */
export const toAccountStudioPickerCapacity = (
  capacity: AgentRegistryDesktopBridgeCapacity | undefined
): AccountStudioPickerCapacity | undefined => {
  if (capacity === undefined) return undefined;
  if (
    capacity.status !==
    AgentRegistryDesktopBridgeCapacityStatus.AGENT_REGISTRY_DESKTOP_BRIDGE_CAPACITY_STATUS_RESOLVED
  ) {
    return { kind: 'unknown' };
  }
  const maximum = capacity.maximum;
  if (!Number.isInteger(maximum) || maximum < 1) return { kind: 'unknown' };
  const committed = Number.isInteger(capacity.committedCount) ? capacity.committedCount : 0;
  const reserved = Number.isInteger(capacity.reservedCount) ? capacity.reservedCount : 0;
  return committed + reserved >= maximum
    ? { kind: 'at_capacity', maximum }
    : { kind: 'available', maximum };
};

export interface AccountStudioPickerState {
  readonly version: 1;
  readonly mode: AccountBootstrapPickerMode;
  readonly localAgentId?: string;
  readonly rooms: readonly AccountStudioPickerRoom[];
  /**
   * ENG-4483 W3: service capabilities from the primary inventory that built
   * this state. Absent when the state came from a replica fallback or an
   * older server; the picker then reads them from its own primary refresh.
   */
  readonly capabilities?: BridgeSetupCapabilities | undefined;
}

/** Convert protobuf bytes before placing bootstrap context in React Router history state. */
export const toAccountStudioPickerState = (
  mode: AccountBootstrapPickerMode,
  rooms: readonly EnsureInitialRoomInventoryRoom[],
  localAgentId: string | undefined,
  capabilities?: BridgeSetupCapabilities | undefined
): AccountStudioPickerState => ({
  version: 1,
  mode,
  ...(localAgentId === undefined ? {} : { localAgentId }),
  ...(capabilities === undefined ? {} : { capabilities }),
  rooms: rooms.flatMap((room) => {
    const roomId = toHexFromId<Room['id']>(room.id);
    const organizationId = toHexFromId(room.organizationId);
    if (roomId === undefined || organizationId === undefined) return [];
    const desktopBridgeCapacity = toAccountStudioPickerCapacity(room.desktopBridgeCapacity);
    return [
      {
        roomId,
        organizationId,
        canLinkBridge: room.canLinkBridge ?? false,
        ...(desktopBridgeCapacity === undefined ? {} : { desktopBridgeCapacity }),
        // Append-only protobuf fields can be absent while clients and the API
        // roll independently; old payloads must remain safe and non-mutating.
        activeBridges: (room.activeBridges ?? []).flatMap((bridge) => {
          const id = toHexFromId(bridge.id);
          return id === undefined ? [] : [{ id, name: bridge.name }];
        })
      }
    ];
  })
});

/** Accept only versioned bootstrap state; the route owns missing-state fallback behavior. */
export const readAccountStudioPickerState = (
  value: unknown
): AccountStudioPickerState | undefined => {
  if (value === null || typeof value !== 'object') return undefined;
  const state = value as Partial<AccountStudioPickerState>;
  if (
    state.version !== 1 ||
    (state.mode !== 'bridge_setup' && state.mode !== 'studio_access') ||
    !Array.isArray(state.rooms)
  ) {
    return undefined;
  }
  return state as AccountStudioPickerState;
};
